import { z } from 'zod';

/**
 * #328 Phase 1 — out-of-app official briefing.
 *
 * Briefing a decision-maker who sits OUTSIDE the ops centre — a mayor / head of
 * council / emergency control group — the Ontario IMS §7.4 / App A #19 "briefing up"
 * of senior and elected officials; App A #21 the ECG "may not be directly involved in an EOC" —
 * — by emailing them the Situation Report OR by recording that they were reached
 * by phone. This is a NAMED doctrinal flow, not an off-app fallback, CONDITIONED
 * on: (a) the send is LOGGED in-COP (App A #72 record-keeping duty) and (b) the
 * content is PLAIN-LANGUAGE (§2.2). The register is INFORM/BRIEF — this never
 * commands the recipient.
 *
 * ONE request shape, discriminated on `channel`:
 *   • email — email the rendered SITREP PDF to one or more officials. Ad-hoc typed
 *     addresses are allowed (no pre-registration) alongside picks from the
 *     delivery-destinations registry / briefing subscribers.
 *   • phone — record that an official was reached by phone (mirrors the emergency-
 *     declaration call_confirmed / call_notes register). No artifact is sent; the
 *     record itself closes the record-keeping duty for the likely-real channel.
 */

/** One out-of-app email recipient. `email` is the address; `label` is the human
 *  name the operator saw (e.g. "City of Elliot Lake — Office of the Mayor") and is
 *  recorded for the audit trail. */
export const BriefOfficialRecipientSchema = z.object({
  email: z.string().email().max(320),
  label: z.string().max(200).optional(),
});
export type BriefOfficialRecipient = z.infer<typeof BriefOfficialRecipientSchema>;

/** Email the SITREP to one or more officials outside the COP. */
export const BriefOfficialEmailRequestSchema = z.object({
  channel: z.literal('email'),
  session_id: z.string().min(1).max(128),
  recipients: z.array(BriefOfficialRecipientSchema).min(1).max(20),
});

/** Record that an official was reached by phone (record-keeping, not a delivery). */
export const BriefOfficialPhoneRequestSchema = z.object({
  channel: z.literal('phone'),
  session_id: z.string().min(1).max(128),
  /** The official reached (a real head of council / ECG member). */
  recipient_name: z.string().min(1).max(200),
  /** The number reached — surfaced from the registry or typed; nullable. */
  recipient_phone: z.string().max(64).nullable().optional(),
  /** Operator-recorded local time of the call, HH:MM (24h). Never machine-stamped —
   *  it is the operator's attestation of when contact was made. */
  reached_at_local: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'reached_at_local must be HH:MM'),
  /** Free-text note on what was conveyed. */
  note: z.string().max(2000).optional(),
});

export const BriefOfficialRequestSchema = z.discriminatedUnion('channel', [
  BriefOfficialEmailRequestSchema,
  BriefOfficialPhoneRequestSchema,
]);
export type BriefOfficialRequest = z.infer<typeof BriefOfficialRequestSchema>;

/**
 * Result of a brief-official action. `sent_at` is the server timestamp the action
 * was recorded (email → "Sent HH:MM" honesty copy). There is DELIBERATELY no
 * acknowledged / read field — an emailed or phoned briefing is SENT, never proven
 * received; the COP must not fabricate an acknowledgement it cannot observe.
 */
export const BriefOfficialResultSchema = z.object({
  briefed: z.boolean(),
  channel: z.enum(['email', 'phone']),
  /** Number of officials briefed (email → recipient count; phone → 1). */
  recipients: z.number().int(),
  /** Email leg: the resolved delivery channel ('email' when SMTP is configured,
   *  else 'console_log' in demo mode). Null for the phone record. */
  delivery_channel: z.enum(['email', 'console_log']).nullable(),
  /** Email leg: the delivery_log row id. Null for the phone record. */
  delivery_log_id: z.string().nullable(),
  /** The in-COP operator_actions record id (present for BOTH channels — the
   *  record-keeping guard, App A #72). */
  record_id: z.string().nullable(),
  /** Server timestamp (ISO) the action was recorded — "Sent HH:MM" for email. */
  sent_at: z.string().nullable(),
});
export type BriefOfficialResult = z.infer<typeof BriefOfficialResultSchema>;

/**
 * #568 — RECORD THE OFFICIAL'S RESPONSE (the paste-back half of the briefing-up loop).
 *
 * A #549 brief-official send (email a SITREP / record a phone contact) records "sent" — but
 * the official's REPLY had nowhere to live. This records what the briefed decision-maker said
 * back, closing the founder §1 workflow-completeness loop (the brief starts AND finishes in the
 * COP with the record kept). Grounded in App A #72 (Scribe: "documenting key activities, events,
 * agreements and any matters of potential legal significance").
 *
 * A RECORD, never proof of receipt — recording a response NEVER claims the official
 * acknowledged / read / approved the Situation Report (the #549 SENT-not-ACK honesty rule holds).
 * There is DELIBERATELY no acknowledged / read field.
 *
 * APPEND-ONLY: multiple responses may be recorded (an official may reply more than once); each is
 * a distinct scribe operator_actions row. A response attaches to a briefing that happened —
 * the route 404s unless the SITREP has at least one prior brief_official record.
 */
export const RecordOfficialResponseRequestSchema = z.object({
  session_id: z.string().min(1).max(128),
  /** The channel the official's response came back on (mirrors the brief-official channels). */
  channel: z.enum(['email', 'phone']),
  /** The official who responded — the human name/label the operator recorded (e.g. "City of
   *  Elliot Lake — Office of the Mayor"). An operator attestation of who replied, not an
   *  identity assertion. */
  official: z.string().min(1).max(200),
  /** Operator-recorded local time the response was received, HH:MM (24h). NEVER machine-stamped
   *  — it is the operator's attestation of when the official replied (mirrors brief-official's
   *  reached_at_local idiom). */
  received_at_local: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'received_at_local must be HH:MM'),
  /** What the official said — the recorded response body. Optional (a response may be logged
   *  with no note, e.g. a bare acknowledgement of receipt). */
  note: z.string().max(2000).optional(),
});
export type RecordOfficialResponseRequest = z.infer<typeof RecordOfficialResponseRequestSchema>;

/**
 * One entry in a SITREP's official-briefing history — a sent brief OR a recorded response, read
 * back from operator_actions (the scribe trail, App A #72), chronological. Normalizes the two
 * operator_actions shapes (brief_official email/phone + record_official_response) into one row
 * the briefing surface renders.
 */
export interface OfficialBriefingHistoryEntry {
  /** operator_actions.id. */
  id: string;
  /** 'brief_official' (a sent briefing) | 'record_official_response' (a recorded reply). */
  kind: 'brief_official' | 'record_official_response';
  /** 'email' | 'phone' — the channel; null only if a legacy row lacks one. */
  channel: 'email' | 'phone' | null;
  /** The counterpart named: recipient label(s) for a sent brief, the official for a response. */
  counterpart: string | null;
  /** The free-text note (what was conveyed on a phone brief / what the official said back). */
  note: string | null;
  /** Operator-attested local time (HH:MM) when present — reached_at_local (phone brief) or
   *  received_at_local (response). Null for an email brief (server-stamped only). */
  attested_local_time: string | null;
  /** The recording operator seat, derived at read time from the stored operator id via the
   *  app's seat-label idiom (never persisted). */
  recorded_by_seat: string;
  /** Server timestamp (ISO 8601 Z) the row was recorded — the chronological ordering key. */
  recorded_at: string;
}

/** GET /api/sitreps/:id/official-briefings — the persisted briefing history, chronological. */
export interface OfficialBriefingHistoryResponse {
  entries: OfficialBriefingHistoryEntry[];
}

/** POST /api/sitreps/:id/record-official-response — returns the newly recorded history entry. */
export interface RecordOfficialResponseResult {
  entry: OfficialBriefingHistoryEntry;
}
